import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationDTO } from '@bifeai/shared';
import { Badge, Button, Card, CardBody, EmptyState, PageHeader, Skeleton } from '@bifeai/ui';
import { BellOff, CheckCheck } from 'lucide-react';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { useSession } from '../lib/session';

const SEVERITY: Record<NotificationDTO['severity'], { label: string; tone: 'info' | 'warning' | 'danger' }> = {
  INFO: { label: 'Aviso', tone: 'info' },
  WARNING: { label: 'Atención', tone: 'warning' },
  CRITICAL: { label: 'Urgente', tone: 'danger' },
};

export function AlertsPage() {
  const { me, selectedBranchId } = useSession();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['notifications', 'list', 100, selectedBranchId],
    queryFn: () => api.get<NotificationDTO[]>('/notifications?limit=100'),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['notifications'] });
  const readOne = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: refresh,
  });
  const readAll = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: refresh,
  });
  const unread = list.data?.filter((n) => !n.read).length ?? 0;
  const branchName = (id: string | null) => (id ? me.branches.find((b) => b.id === id)?.name : null);

  return (
    <>
      <PageHeader
        title="Alertas"
        subtitle="Mercadería por vencer, stock bajo, pagos a proveedores y diferencias de caja."
        actions={
          unread > 0 && (
            <Button icon={<CheckCheck />} onClick={() => readAll.mutate()} loading={readAll.isPending}>
              Marcar todas como leídas
            </Button>
          )
        }
      />
      <Card>
        <CardBody flush>
          {list.isPending && (
            <div style={{ padding: '8px 22px 16px', display: 'grid', gap: 12 }}>
              <Skeleton />
              <Skeleton />
            </div>
          )}
          {list.data?.length === 0 && (
            <EmptyState icon={<BellOff />} title="No hay alertas" text="Cuando algo necesite tu atención, aparece acá y en la campanita." />
          )}
          {list.data?.map((n) => (
            <div key={n.id} className="bf-notif" data-read={n.read} style={{ cursor: 'default', padding: '16px 22px' }}>
              <span className={`bf-notif-dot bf-notif-dot--${n.severity}`} aria-hidden />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                  }}
                >
                  <p className="bf-notif-title">{n.title}</p>
                  <Badge tone={SEVERITY[n.severity].tone}>{SEVERITY[n.severity].label}</Badge>
                  {branchName(n.branchId) && <Badge>{branchName(n.branchId)}</Badge>}
                </div>
                {n.body && (
                  <p className="bf-notif-body" style={{ fontSize: 'var(--bf-text-sm)' }}>
                    {n.body}
                  </p>
                )}
                <p className="bf-notif-time">{formatDateTime(n.createdAt)}</p>
              </div>
              {!n.read && (
                <Button size="sm" variant="ghost" onClick={() => readOne.mutate(n.id)}>
                  Marcar como leída
                </Button>
              )}
            </div>
          ))}
        </CardBody>
      </Card>
    </>
  );
}
